import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Skeleton } from "../../../components/ui/skeleton/skeleton";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "../../../components/ui/toggle-group/toggle-group";
import { useMTUs } from "../../../queries/mtu/use-mtus";
import { useFailures } from "../../../queries/nc/use-failures";
import { useNCSeverity } from "../../../queries/nc/use-nc-severity";
import { DocumentSelector } from "../../document-selector/document-selector";
import { Textarea } from "../../../components/ui/textarea/textarea";
import { FailureMTUList } from "../../../components/ui/failure-mtu-list/failure-mtu-list";
import { QueryError } from "@/components/ui/query-error/query-error";

const detailsSchema = (t: TFunction<"translation">) =>
  z.object({
    severity: z.string().min(1, t("nc.details.severity.error")),
    note: z.string().optional(),
    documents: z.array(z.string()),
  });

export const useDetailsForm = () => {
  const { t } = useTranslation();
  return useForm<z.infer<ReturnType<typeof detailsSchema>>>({
    resolver: zodResolver(detailsSchema(t)),
    defaultValues: {
      note: "",
      severity: "",
      documents: [],
    },
  });
};

export function AddDetails({
  form,
  failureNIds,
  mtuCodes,
}: {
  form: ReturnType<typeof useDetailsForm>;
  failureNIds: string[];
  mtuCodes: string[];
}) {
  const { t } = useTranslation();
  const {
    severities,
    isPending,
    error: severityError,
    refetch: refetchSeverity,
  } = useNCSeverity();
  const {
    failures,
    error: transitionError,
    refetch: refetchTransitions,
  } = useFailures();
  const { MTUs, error: mtusError, refetch: refetchMtus } = useMTUs();

  const selectedFailures = failures?.filter((x) =>
    failureNIds.includes(x.NId ?? x.Id)
  );
  const selectedMTUs = MTUs?.filter((x) =>
    mtuCodes.includes(
      x.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code ?? x.Id
    )
  );

  if (severityError || transitionError || mtusError) {
    const retries = [
      severityError && refetchSeverity,
      transitionError && refetchTransitions,
      mtusError && refetchMtus,
    ];

    return (
      <QueryError
        errors={[severityError, transitionError, mtusError]}
        retries={retries}
      />
    );
  }

  return (
    <Form {...form}>
      <div className="flex flex-col gap-6">
        <FailureMTUList
          mtus={selectedMTUs?.map((x) => ({
            code: x.DM_MaterialTrackingUnit.MaterialTrackingUnit.Code ?? "",
            id: x.Id ?? "",
          }))}
          failures={selectedFailures?.map((x) => ({
            code: x.NId ?? "",
            id: x.Id ?? "",
            name: x.Name ?? "",
          }))}
        />
        <div className="grid grid-cols-2 gap-x-4 gap-y-4">
          <FormField
            control={form.control}
            name="severity"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="font-semibold">
                  {t("nc.details.severity.label")}*
                </FormLabel>
                <FormControl>
                  <ToggleGroup
                    type="single"
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex"
                  >
                    {isPending &&
                      [...Array(3)].map((_, i) => (
                        <Skeleton className="h-[42px] flex-1" key={i} />
                      ))}
                    {severities?.map((x) => (
                      <ToggleGroupItem
                        className="flex-1 capitalize"
                        value={x.NId ?? ""}
                        id={x.NId ?? ""}
                        key={x.Id}
                      >
                        {x.Name?.toLowerCase()}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold">
                {t("nc.details.note.label")}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder={t("nc.details.note.placeholder")}
                  {...field}
                  className="max-w-[50%]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="documents"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold">
                {t("nc.details.documents.label")}
              </FormLabel>
              <FormControl>
                <DocumentSelector
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </Form>
  );
}
